import {useState} from 'react';
import {
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    Title,
    UnstyledButton,
} from '@mantine/core';
import {
    AiOutlineUserAdd,
    AiOutlineFileAdd,
    AiOutlineAppstoreAdd,
    AiOutlinePlusCircle,
} from 'react-icons/ai';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../../config/rbac/actions';
import {AddFormDrawer as AddCustomerDrawer} from '../../../customers/drawers/AddFormDrawer';
import {AddFormDrawer as AddInvoiceDrawer} from '../../../invoices/drawers/AddFormDrawer';
import {AddFormDrawer as AddProductDrawer} from '../../../products/drawers/AddFormDrawer';
import {AddFormDrawer as AddSubscriptionDrawer} from '../../../subscriptions/drawers/AddFormDrawer';

type DrawerKey = 'customer' | 'invoice' | 'product' | 'subscription';

const ActionButton = ({icon, label, onClick}: { icon: React.ReactNode, label: string, onClick: () => void }) => (
    <UnstyledButton
        onClick={onClick}
        p="md"
        style={{
            border: '1px solid var(--mantine-color-gray-3)',
            borderRadius: 'var(--mantine-radius-md)',
            textAlign: 'center',
        }}
    >
        <Stack align="center" gap={4}>
            <Group justify="center" style={{fontSize: 24, color: 'var(--mantine-color-blue-6)'}}>
                {icon}
            </Group>
            <Text size="xs" fw={500}>{label}</Text>
        </Stack>
    </UnstyledButton>
);

export const QuickActionsWidget = () => {

    const {allowedActions} = useVerifiedPermissions();
    const [openDrawer, setOpenDrawer] = useState<DrawerKey | null>(null);

    const actions: { key: DrawerKey, icon: React.ReactNode, label: string, permission: string }[] = [];

    if (allowedActions.includes(rbacActions.CreateCustomer)) {
        actions.push({key: 'customer', icon: <AiOutlineUserAdd/>, label: 'New Customer', permission: rbacActions.CreateCustomer});
    }
    if (allowedActions.includes(rbacActions.CreateInvoice)) {
        actions.push({key: 'invoice', icon: <AiOutlineFileAdd/>, label: 'New Invoice', permission: rbacActions.CreateInvoice});
    }
    if (allowedActions.includes(rbacActions.CreateProduct)) {
        actions.push({key: 'product', icon: <AiOutlineAppstoreAdd/>, label: 'New Product', permission: rbacActions.CreateProduct});
    }
    if (allowedActions.includes(rbacActions.CreateSubscription)) {
        actions.push({key: 'subscription', icon: <AiOutlinePlusCircle/>, label: 'New Subscription', permission: rbacActions.CreateSubscription});
    }

    if (actions.length === 0) return null;

    return (
        <>
            <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Stack gap="md">
                    <Title order={5}>Quick Actions</Title>
                    <SimpleGrid cols={{base: 2, sm: 4}}>
                        {actions.map(action => (
                            <ActionButton
                                key={action.key}
                                icon={action.icon}
                                label={action.label}
                                onClick={() => setOpenDrawer(action.key)}
                            />
                        ))}
                    </SimpleGrid>
                </Stack>
            </Paper>

            <AddCustomerDrawer
                opened={openDrawer === 'customer'}
                onClose={() => setOpenDrawer(null)}
            />
            <AddInvoiceDrawer
                opened={openDrawer === 'invoice'}
                onClose={() => setOpenDrawer(null)}
            />
            <AddProductDrawer
                opened={openDrawer === 'product'}
                onClose={() => setOpenDrawer(null)}
            />
            <AddSubscriptionDrawer
                opened={openDrawer === 'subscription'}
                onClose={() => setOpenDrawer(null)}
            />
        </>
    );
};
